import { useAuth } from "@clerk/clerk-react";
import type { SelfServePlanKey } from "@giftforward/entitlements";
import CloseIcon from "@mui/icons-material/Close";
import LockOpenIcon from "@mui/icons-material/LockOpen";
import Dialog from "@mui/material/Dialog";
import { useEffect, useState } from "react";
import PlanActionModal from "@/components/plan/PlanActionModal";
import PlanTierCards from "@/components/plan/PlanTierCards";
import PlanUpgradeDrawer from "@/components/plan/PlanUpgradeDrawer";
import { CONFIGS } from "@/constants/GfgModal";
import { PLAN_MODAL_STRINGS } from "@/constants/Plans";
import type { PlanUpsellContext } from "@/context/CampaignBuilderContext";
import { useCampaignBuilder } from "@/context/CampaignBuilderContext";
import { useMyEntitlements } from "@/hooks/useMyEntitlements";
import { usePermissions } from "@/hooks/usePermissions";
import { usePlanActions } from "@/hooks/usePlanActions";
import { usePlans } from "@/hooks/usePlans";
import { useTier } from "@/hooks/useTier";

/** GfgModal fires `onClose` BEFORE `onPrimary`, so an informational modal needs no real action. */
const NO_PRIMARY_ACTION = () => {};

/**
 * `onDowngrade` and `onCancelDowngrade` are UNREACHABLE in variant="upsell". PlanTierCards'
 * `ctaForUpsell` only ever emits: a plain label for the current tier ("Current plan") and for
 * tiers below it ("Included"), `onTalkToSales` for enterprise, and `onSelectPlan` for the
 * self-serve plans — it never reads either downgrade callback, and it ignores `pendingDowngrade` entirely
 * ("this modal only ever sells up"). The props are required, so they get a no-op.
 *
 * They used to point at navigate("/account"), which IS the redirect GFG-239 removes. Keeping dead
 * navigation "just in case" would re-introduce the bug the moment `ctaForUpsell` grew a downgrade
 * button, so it is gone. If a downgrade CTA is ever wanted in the upsell matrix, `ctaForUpsell` has
 * to start emitting it AND a real handler has to land here — a button that silently does nothing is
 * the only outcome worse than the redirect.
 */
const UNREACHABLE_IN_UPSELL = () => {};

/**
 * The in-app plan-upgrade modal (DIV-7761 Phase 40). Replaces the standalone /plan route so
 * campaign-builder sessions never navigate away: every "upgrade" gate across the builder opens
 * this instead. Upsell only — hero + optional context pill + the tier cards, no logo
 * ticker / testimonials / "talk to our team" footer (those died with the page).
 *
 * Rendered as a fullscreen MUI Dialog (the repo's modal primitive — same as GfgModal): MUI owns
 * the portal to <body>, the focus trap, Escape handling, and — via its ModalManager — the
 * body-scroll-lock and aria-hidden handoff with the GfgModal that opens this, which a
 * hand-rolled overlay had to work around.
 *
 * GFG-239: this component now also HOSTS checkout. Every CTA used to call
 * navigate("/account", { state: { tab: "plan" } }), which unmounted the whole builder tree and
 * dumped the user on Account > Plan — they lost the step they were gated on and the URL changed.
 * Instead, PlanUpgradeDrawer (the same drawer Account > Plan mounts) is opened ON TOP of this
 * Dialog. The builder stays mounted underneath the whole time, so paying never touches the router;
 * the ["entitlements"] invalidation inside usePlanActions is what unlocks the gated feature behind
 * it, since the campaign-builder gates read the entitlement matrix. On the same success it also
 * invalidates ["tier"], which is what refreshes the billing dates, and ["orgSubscription"] for the
 * GFG-side Subscription tab.
 */
export default function PlanModal() {
  const { planModal, closePlanModal } = useCampaignBuilder();
  // The plan comes from entitlements, which any member of the organization may read; the renewal
  // date and the scheduled-cancellation flag are billing and stay on /tier. /tier is gated on TIER
  // view, so Manager and View Only get a 403 from it — and this modal opens from the campaign
  // builder, where exactly those roles see it. They still get the right current plan on the cards,
  // and simply no cancellation/renewal detail.
  const { ent } = useMyEntitlements();
  const { renewalDate, cancelAtPeriodEnd, isLoading } = useTier();
  const plan = ent?.plan.key;
  const isEnterprise = plan === "enterprise";
  const { can } = usePermissions();
  const { actor } = useAuth();
  const { upgrade, cancel, setAutoRenew } = usePlanActions();
  const { byKey } = usePlans();
  const planName = plan ? (byKey(plan)?.display.name ?? "") : "";

  const isOpen = planModal !== null;

  // Freeze the upsell context while the close transition plays so the context pill doesn't
  // vanish mid-fade — same pattern as CampaignBuilderModal.
  const [stableContext, setStableContext] = useState<PlanUpsellContext | null>(
    planModal?.context ?? null,
  );
  useEffect(() => {
    if (planModal !== null) setStableContext(planModal.context);
  }, [planModal]);

  /** The plan checkout is open for; null means the drawer is closed. */
  const [checkoutPlan, setCheckoutPlan] = useState<SelfServePlanKey | null>(null);
  const drawerOpen = checkoutPlan !== null;
  /** Enterprise has no self-service plan change — the only PlanActionModal context reachable here. */
  const [enterpriseModalOpen, setEnterpriseModalOpen] = useState(false);
  /**
   * "The drawer closed" and "the upgrade succeeded" are different events, so the drawer's
   * `onUpgraded` seam is recorded here rather than acted on immediately: a user who opens checkout
   * and backs out must land back on the tier cards, while a user who paid and dismissed the success
   * state must land back in the builder (AC2). Reset on every open so a second visit to the drawer
   * can't inherit the first one's verdict.
   */
  const [upgraded, setUpgraded] = useState(false);

  const isImpersonating = !!actor;
  // Money paths fail closed: an impersonated session may look but never pay, and a caller without
  // TIER edit never gets a live CTA. The three `isPending` flags are this component's own mutation
  // observers: PlanModal fires none of them today (the drawer owns the upgrade call and its own
  // in-flight lock), so they read false — they are here so that adding any plan mutation to this
  // file inherits the guard instead of quietly bypassing it.
  //
  // One deliberate difference from PlanPanel's `actionsDisabled`: the readiness terms are folded in
  // HERE instead of gating the render. `isLoading` (the /tier read) and `plan` (entitlements-derived)
  // now come from two INDEPENDENT queries, so BOTH have to have landed before any CTA goes live —
  // hence `isLoading || !plan`. `!plan` is preferred over destructuring the ENTITLEMENTS query's
  // own `isLoading` flag, because it is strictly stronger than that flag: it also covers
  // entitlements erroring out, and the case where its query never runs at all (`enabled: false`
  // while `organizationId` is null), where that flag stays false forever. It says nothing about the
  // /tier `isLoading` above, which observes a separate query — neither term implies the other, so
  // both stay. Without `!plan`, PlanTierCards sees `plan === undefined`, matches no tier as current,
  // and renders live, primary "Get Pro" / "Get Pro Plus" buttons, with the Enterprise card falling
  // through to a live (non-primary) "Talk to sales" — to an Enterprise or already-paying
  // organization included.
  // PlanPanel spends the same two terms on a spinner (`if (isLoading || !plan) return <spinner>`);
  // this modal is opened BY a gate as an upsell surface, so swapping the tier cards for a spinner
  // would read as a broken gate. Disabling the CTAs buys the same protection without hiding the pitch.
  const actionsDisabled =
    isImpersonating ||
    !can("edit", "TIER") ||
    isLoading ||
    !plan ||
    upgrade.isPending ||
    cancel.isPending ||
    setAutoRenew.isPending;

  /** "Get Pro" / "Get Pro Plus" → checkout. Enterprise never self-serves: plan changes are contract-run. */
  function handleSelectPlan(target: SelfServePlanKey) {
    // The `disabled` prop below already keeps this CTA out of the tab order while the drawer is
    // open, but the guard stays here too — defence in depth for any caller that ever fires this
    // handler directly instead of through the (native-disabled) button.
    if (actionsDisabled || drawerOpen) return;
    if (isEnterprise) {
      // Defence in depth: for an Enterprise account `ctaForUpsell` labels every card below it
      // instead of rendering a button, so this cannot fire today. While `plan` is unresolved `isEnterprise`
      // is false anyway, so the branch is unreachable then too — what protects that window is the
      // `!plan` term in `actionsDisabled`, which stops the guard above from ever reaching the
      // drawer. It mirrors PlanPanel.handleSelectPlan so that if the matrix ever offers Get Pro to
      // an Enterprise account, it opens a modal rather than charging them the Pro price.
      setEnterpriseModalOpen(true);
      return;
    }
    setUpgraded(false);
    setCheckoutPlan(target);
  }

  /**
   * Enterprise's "Talk to sales". PlanPanel answers this with a toast, but this modal has no
   * notice system and inventing one here is out of GFG-239's scope, so it reuses the existing
   * informational enterprise modal (single dismiss, no payment, no navigation — AC5).
   */
  function handleTalkToSales() {
    // Same defence-in-depth as handleSelectPlan: the drawer, not the tier cards behind it, owns the
    // interaction while it's open.
    if (drawerOpen) return;
    setEnterpriseModalOpen(true);
  }

  function handleDrawerClose() {
    setCheckoutPlan(null);
    // Paid and dismissed → the user is done with plans; drop this modal too so they are returned
    // to the exact builder step they were gated on, now unlocked. Backed out without paying →
    // only the drawer peels off and the tier cards are still there behind it.
    if (upgraded) closePlanModal();
  }

  /**
   * Backdrop, Escape and the X button all land here. While the drawer is on top it owns dismissal
   * (AC7): without this guard one Escape would close the upsell surface UNDERNEATH the open
   * checkout — Escape reaches both this Dialog and the drawer's own window listener — leaving a
   * payment sheet over a builder step with nothing behind it to return to. The drawer's DOM
   * survives either way (it is a sibling body portal with its own `open`, and PlanModal is mounted
   * unconditionally by CampaignBuilder), and an in-flight request is never interrupted, because
   * `drawerOpen` stays true for the whole processing state.
   *
   * `enterpriseModalOpen` deliberately does NOT need the same guard: that one is a GfgModal, so
   * MUI's ModalManager already routes Escape to the topmost modal only and its backdrop covers this
   * Dialog's controls. The drawer is a hand-rolled overlay with its own window keydown listener,
   * outside that manager — which is exactly why it has to be guarded by hand.
   */
  function handleDialogClose() {
    if (drawerOpen) return;
    closePlanModal();
  }

  const contextConfig = stableContext ? CONFIGS[stableContext] : null;

  return (
    <>
      <Dialog
        fullScreen
        open={isOpen}
        onClose={handleDialogClose}
        aria-labelledby="plan-modal-title"
        // The drawer is a sibling portal in <body>, i.e. OUTSIDE this Dialog's focus trap, so
        // MUI's enforce-focus would yank focus off the drawer panel the moment it opens (and off
        // any Stripe Elements input inside it) and back into the tier cards underneath. Releasing
        // the trap only while the drawer is up keeps the Dialog trapped the rest of the time.
        disableEnforceFocus={drawerOpen}
        slotProps={{
          paper: {
            sx: {
              bgcolor: "var(--color-clear-dust)",
              fontFamily: "var(--font-lato)",
              overscrollBehavior: "contain",
            },
          },
        }}
      >
        <div className="mx-auto w-full max-w-[1040px] px-6 pt-14 pb-20">
          <button
            type="button"
            onClick={handleDialogClose}
            aria-label="Close"
            className="fixed top-5 right-6 z-10 flex h-10 w-10 cursor-pointer items-center justify-center rounded-full border border-card-border bg-white text-rodeo-black shadow-[0_2px_8px_rgba(0,0,0,0.08)] hover:bg-wax"
          >
            <CloseIcon />
          </button>

          <div className="mb-10 text-center">
            <h2
              id="plan-modal-title"
              className="mb-3 font-crimson text-4xl leading-[1.25] font-normal text-rodeo-black"
            >
              {PLAN_MODAL_STRINGS.heroPrefix}
              <em className="not-italic uppercase tracking-[1px]">
                {PLAN_MODAL_STRINGS.heroEmphasis}
              </em>
            </h2>
            <p className="mx-auto max-w-[600px] text-[15px] leading-[1.6] text-graphite">
              {PLAN_MODAL_STRINGS.subtitle}
            </p>

            {contextConfig && (
              <div className="mt-[18px] inline-flex items-center gap-1.5 rounded-full border border-card-border bg-white px-4 py-2 text-[13px] font-semibold text-rodeo-black">
                <LockOpenIcon className="text-[16px]" />
                {contextConfig.title}
              </div>
            )}
          </div>

          <div className="mt-10">
            <PlanTierCards
              variant="upsell"
              plan={plan}
              pendingDowngrade={cancelAtPeriodEnd}
              onSelectPlan={handleSelectPlan}
              onDowngrade={UNREACHABLE_IN_UPSELL}
              onCancelDowngrade={UNREACHABLE_IN_UPSELL}
              onTalkToSales={handleTalkToSales}
              // `|| drawerOpen`: once the drawer is on top, these buttons are visually hidden
              // underneath it but stay mounted, non-`aria-hidden`, and — since `disableEnforceFocus`
              // above releases the Dialog's own focus trap so the drawer/Stripe inputs stay
              // reachable — no longer excluded from the tab order either. Without this, Shift+Tab
              // out of the drawer can land on "Get Pro"/"Talk to sales". A native `disabled` button
              // is un-focusable, so this keeps controls that are visually covered by the drawer out
              // of the tab order without a hand-rolled aria-hidden/inert sweep. The re-fire itself
              // is already inert: both handlers early-return on `drawerOpen` before touching any
              // state, so this prop is about focus order, not about protecting `upgraded`.
              disabled={actionsDisabled || drawerOpen}
            />
          </div>
        </div>
      </Dialog>

      {/*
        Stacking, verified rather than assumed: this Dialog's MUI Modal root is a direct child of
        <body> at theme.zIndex.modal (1300) — MUI's ModalManager does not renumber nested modals.
        The drawer createPortal's into the same <body> only once `drawerOpen` flips, so React
        appends its nodes AFTER the already-mounted Dialog root; its panel is z-[1301] (> 1300) and
        its backdrop z-[1300] ties and wins on document order. So the drawer paints above without
        anyone touching a z-index. Rejected: dropping the Dialog to `theme.zIndex.modal - 1` — the
        gate's GfgModal is still fading out at 1300 when this opens, so the Dialog would slide under
        it for the length of that transition, trading a real bug for an imaginary one.

        Rendered as a SIBLING of the Dialog, not a child of it: a child would sit inside the
        Dialog's focus trap and inside the subtree its close transition unmounts, and — since React
        commits effects child-before-parent — it would focus itself before the trap had processed
        the released `disableEnforceFocus`.
      */}
      <PlanUpgradeDrawer
        plan={checkoutPlan}
        onClose={handleDrawerClose}
        disabled={actionsDisabled}
        onUpgraded={() => setUpgraded(true)}
      />

      {/* Informational only — "enterprise" is the sole context reachable from an upsell matrix. */}
      <PlanActionModal
        context={enterpriseModalOpen ? "enterprise" : null}
        onClose={() => setEnterpriseModalOpen(false)}
        onPrimary={NO_PRIMARY_ACTION}
        renewalDate={renewalDate}
        planName={planName}
      />
    </>
  );
}
